import {generateTaskPathForRepo} from '@github-ui/agent-sessions/utils/generate-task-path'
import {announce} from '@github-ui/aria-live'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {EMBEDDED_MENU_PORTAL_CLASS, MenuPortalContainer} from '@github-ui/copilot-chat/components/PortalContainerUtils'
import {TraceDisplay} from '@github-ui/copilot-chat/components/tracing/TraceDisplay'
import {useTraceState} from '@github-ui/copilot-chat/components/tracing/TraceProvider'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {useSelectedCopilotSpaceId} from '@github-ui/copilot-chat/hooks/use-selected-custom-copilot-id'
import {useChatStateValues} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {getActiveMessages} from '@github-ui/copilot-chat-helpers/copilot-chat-subthreading-helpers'
import {CopilotLocalStorage, copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import type {CopilotChatMessage, CopilotChatReference, CopilotSpaceId} from '@github-ui/copilot-chat-types'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {ReadOnlyConversationNotice} from '@github-ui/copilot-shared-ui/components/ReadOnlyConversationNotice'
import {useFetchCopilotSpace} from '@github-ui/copilot-spaces/hooks'
import {useSpaceSelection} from '@github-ui/copilot-spaces/hooks/use-space-selection'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useNavigate} from '@github-ui/react-router'
import {useSafeAsyncCallback} from '@github-ui/use-safe-async-callback'
import type {AnchorSide} from '@primer/behaviors'
import {GitBranchIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useHandleMessageSubmit} from '../hooks/use-handle-message-submit'
import {useIsSharedThread} from '../hooks/use-is-shared-thread'
import {ChatInputContainer} from './ChatInputContainer'
import styles from './ConversationView.module.css'
import {ErrorFallback} from './ErrorFallback'
import {ImmersiveChat} from './ImmersiveChat'
import {NewConversation} from './NewConversation/NewConversation'
import {NewTopicNudge} from './NewTopicNudge'
import {NotificationsSection} from './NotificationsSection'
import {spaceConversationVariants, useSpaceConversationStatus} from './Spaces/SpaceConversationBanner'

interface ConversationViewProps {
  isMobile: boolean
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  attachmentMenuSide?: AnchorSide
  /** Force icon-only mode in the toolbar, useful for container-based responsive behavior */
  forceIconOnly?: boolean
  /** Whether this is the embedded immersive view (affects which suggestions are shown) */
  isEmbedded?: boolean
  /** Replaces the composer with a notice for chats that cannot be continued in this client. */
  readOnly?: boolean
}

function ConversationViewInner({
  isMobile,
  textAreaRef,
  attachmentMenuSide,
  forceIconOnly,
  isEmbedded,
  readOnly = false,
}: ConversationViewProps) {
  const state = useChatStateValues(
    'apiUrl',
    'messages',
    'messagesLoading',
    'threadsLoading',
    'selectedThreadID',
    'currentTopic',
    'pendingFirstMessage',
    'shouldReplaceHistoryOnNavigation',
    'currentReferences',
    'sharedThreadChannel',
    'mode',
  )
  const manager = useChatManager()
  const traceState = useTraceState()
  const {messages, messagesLoading, threadsLoading} = state
  const {chatQuotaExceeded} = useEntitlement()
  const customCopilotId = useSelectedCopilotSpaceId()
  const {data: customCopilot} = useFetchCopilotSpace(customCopilotId)
  const customCopilotExists = Boolean(customCopilot)
  const {variant: spaceViewVariant} = useSpaceConversationStatus(customCopilotId, state.selectedThreadID)
  const spaceSizeExceeded = spaceViewVariant === spaceConversationVariants.customCopilotSizeExceeded
  const showChatInput = (!spaceViewVariant || customCopilotExists) && !spaceSizeExceeded
  const {handleSpaceSelect} = useSpaceSelection()

  const currentTopicRef = useRef(state.currentTopic)
  const isSharedThread = useIsSharedThread()
  const hasEntrypointMessage = useMemo(
    () => !state.selectedThreadID && copilotLocalStorage.getEntrypointMessage(),
    [state.selectedThreadID],
  )
  const shouldShowNewConversation = useMemo(() => {
    if (
      state.selectedThreadID || // If there's a selected thread, we know it's not a new conversation
      state.pendingFirstMessage || // If the user sent a message but the thread hasn't been created yet, show the ImmersiveChat
      hasEntrypointMessage || // If there's an entrypoint message, kick off chat right away
      customCopilotId || // Custom copilots are currently still handled in the ImmersiveChat, should probably be moved up to Layout
      messagesLoading.state === 'error' || // Don't show if there's an error state because we do show the input at the bottom then
      threadsLoading.state === 'error' ||
      state.shouldReplaceHistoryOnNavigation // Don't show if we're about to reroute to a thread.
    ) {
      return false
    }

    return true
  }, [
    state.selectedThreadID,
    state.pendingFirstMessage,
    hasEntrypointMessage,
    customCopilotId,
    messagesLoading.state,
    threadsLoading.state,
    state.shouldReplaceHistoryOnNavigation,
  ])

  const nextMessageIndex = messages.length

  const handleUserSubmit = useHandleMessageSubmit(currentTopicRef, nextMessageIndex)

  useEffect(() => {
    currentTopicRef.current = state.currentTopic
  }, [state.currentTopic])

  useEffect(() => {
    const sendMessage = async (message: NonNullable<ReturnType<CopilotLocalStorage['getEntrypointMessage']>>) => {
      copilotLocalStorage.clearEntrypointMessage(state.selectedThreadID)

      // If the message has a space reference, select that space before submitting the message
      const space = message?.references?.find?.(ref => ref.type === 'copilot-space')
      const copilotSpaceId: CopilotSpaceId | null = space ? {id: space.id, owner: space.owner} : null
      if (copilotSpaceId) handleSpaceSelect(copilotSpaceId)

      if (message.model) {
        manager.selectModel(message.model)
      }

      if (!state.selectedThreadID) {
        // Set flag to replace history on navigation since this is from an entrypoint
        manager.setShouldReplaceHistoryOnNavigation(true)
      }

      let thread = state.selectedThreadID ? manager.getThreadById(state.selectedThreadID) : null
      if (state.selectedThreadID && !thread && state.threadsLoading.state !== 'loaded') {
        // if the threads haven't loaded yet, wait for them to load so we send to the correct thread
        const threads = await manager.fetchThreads()
        thread = threads?.find(t => t.id === state.selectedThreadID) || null
      }

      // If the thread has no messages, this is the first message of a new thread originated
      // from the dashboard entrypoint. The dashboard flow uses noSend:true to pre-create the
      // thread and then navigates here, so the normal !thread guard in sendChatMessage never
      // runs on this manager instance. Mark it so first_message_rendered fires on completion.
      if (messages.length === 0) {
        manager.markFirstMessageInNewThread()
      }

      // eslint-disable-next-line @eslint-react/web-api-no-leaked-timeout
      setTimeout(() => {
        void handleUserSubmit(
          message.message,
          message.references,
          message.model,
          copilotSpaceId, // Pass copilotSpaceId explicitly because React state updates (from handleSpaceSelect above) are asynchronous and may not be reflected in activeCopilotSpaceId yet. This ensures the message is sent with the correct space.
          message.mediaContent,
          thread,
        )
      }, 100)
    }

    const message = copilotLocalStorage.getEntrypointMessage(state.selectedThreadID)
    if (message && !chatQuotaExceeded) {
      void sendMessage(message)
    }
  }, [
    handleUserSubmit,
    chatQuotaExceeded,
    state.currentReferences,
    manager,
    handleSpaceSelect,
    state.selectedThreadID,
    state.threadsLoading.state,
    messages.length,
  ])

  const content = (
    <div className={clsx(styles.root, {[styles.embeddedRoot]: isEmbedded})}>
      {traceState.traceInfo?.visible ? (
        <TraceDisplay sharedThreadChannel={state.sharedThreadChannel} threadId={state.selectedThreadID} />
      ) : (
        <>
          <div className={styles.container}>
            {shouldShowNewConversation ? (
              <NewConversation
                textAreaRef={textAreaRef}
                handleUserSubmit={handleUserSubmit}
                nextMessageIndex={nextMessageIndex}
                forceIconOnly={forceIconOnly}
                isEmbedded={isEmbedded}
              />
            ) : (
              <ImmersiveChat inputRef={textAreaRef} isMobile={isMobile} readOnly={readOnly} />
            )}
          </div>
          {(!isSharedThread || copilotFeatureFlags.copilotDuplicateThread) && !shouldShowNewConversation && (
            <div className={styles.footer}>
              {state.mode === 'assistive' && <MenuPortalContainer className={EMBEDDED_MENU_PORTAL_CLASS} />}
              {readOnly ? (
                messagesLoading.state === 'loaded' ? (
                  <ReadOnlyChatNotice
                    key={state.selectedThreadID}
                    apiURL={state.apiUrl}
                    messages={getActiveMessages(messages)}
                    legacyThreadId={state.selectedThreadID ?? ''}
                    legacyThreadName={
                      (state.selectedThreadID && manager.getThreadById(state.selectedThreadID)?.name) || 'Legacy chat'
                    }
                  />
                ) : null
              ) : !showChatInput ? (
                <NotificationsSection />
              ) : (
                <>
                  <NewTopicNudge composerRef={textAreaRef} />
                  <ChatInputContainer
                    textAreaRef={textAreaRef}
                    handleUserSubmit={handleUserSubmit}
                    nextMessageIndex={nextMessageIndex}
                    slashCommandsEnabled
                    isNewConversation={false}
                    attachmentMenuSide={attachmentMenuSide}
                    forceIconOnly={forceIconOnly}
                  />
                </>
              )}
            </div>
          )}
          {isEmbedded && shouldShowNewConversation && (
            <div className={styles.footer}>
              {state.mode === 'assistive' && <MenuPortalContainer className={EMBEDDED_MENU_PORTAL_CLASS} />}
              <ChatInputContainer
                textAreaRef={textAreaRef}
                handleUserSubmit={handleUserSubmit}
                nextMessageIndex={nextMessageIndex}
                slashCommandsEnabled
                isNewConversation
                showDisabledInputOnQuotaExceeded
                attachmentMenuSide={attachmentMenuSide}
                forceIconOnly={forceIconOnly}
              />
            </div>
          )}
        </>
      )}
    </div>
  )

  return content
}

function legacyConversationMessages(messages: readonly CopilotChatMessage[]) {
  return messages.map(message => ({
    role: message.role,
    content:
      message.contentParts && message.contentParts.length > 0
        ? message.contentParts
            .filter(part => part.type === 'text')
            .map(part => part.content)
            .join('')
        : message.content || '',
  }))
}

function ownerFromNameWithOwner(nameWithOwner: string | undefined): string[] {
  const parts = nameWithOwner?.split('/')
  return parts?.length === 2 && parts[0] && parts[1] ? [parts[0]] : []
}

function stringProperty(value: unknown, property: string): string | undefined {
  if (!value || typeof value !== 'object' || !(property in value)) return undefined
  const propertyValue = (value as Record<string, unknown>)[property]
  return typeof propertyValue === 'string' ? propertyValue : undefined
}

function legacyReferenceOwners(reference: CopilotChatReference): string[] {
  switch (reference.type) {
    case 'repository':
      return [reference.ownerLogin]
    case 'file':
    case 'file-v2':
    case 'folder':
    case 'snippet':
    case 'workspace-terminal-log':
    case 'task-request':
    case 'job':
      return [reference.repoOwner]
    case 'symbol': {
      const owner = stringProperty(reference, 'repoOwner')
      return owner ? [owner] : []
    }
    case 'file-changes':
    case 'commit':
    case 'issue':
    case 'discussion':
    case 'release':
      return [reference.repository.owner]
    case 'pull-request':
      return [reference.repository.ownerLogin]
    case 'file-diff':
      return [reference.baseFile?.repoOwner, reference.headFile?.repoOwner].filter(
        (owner): owner is string => owner !== undefined,
      )
    case 'copilot-space':
    case 'org-instructions':
      return [reference.owner]
    case 'pr-overview':
      return ownerFromNameWithOwner(reference.repositoryNwo)
    case 'pull-request.api':
    case 'issue.api':
    case 'alert.api':
      return ownerFromNameWithOwner(reference.repo)
    case 'api-response':
      return 'repo' in reference.data ? ownerFromNameWithOwner(reference.data.repo) : []
    case 'github.issue-list':
    case 'github.pr-list':
    case 'github.task-list':
      return reference.items.flatMap(item => ownerFromNameWithOwner(item.repository))
    default:
      return []
  }
}

function legacyConversationOwners(messages: readonly CopilotChatMessage[]): string[] {
  const owners = new Map<string, string>()

  for (const message of messages) {
    const references = [
      ...(message.references ?? []),
      ...(message.skillExecutions?.flatMap(execution => execution.references ?? []) ?? []),
      ...(message.contentParts?.flatMap(part =>
        part.type === 'toolCall' ? (part.skillExecution.references ?? []) : [],
      ) ?? []),
    ]
    for (const reference of references) {
      for (const owner of legacyReferenceOwners(reference)) {
        const normalizedOwner = owner.trim()
        const ownerKey = normalizedOwner.toLowerCase()
        if (normalizedOwner && !owners.has(ownerKey)) owners.set(ownerKey, normalizedOwner)
      }
    }
  }

  return [...owners.values()]
}

const READ_ONLY_CHAT_ERROR = 'The new session could not be created. Please try again.'
const MULTIPLE_REFERENCE_OWNERS =
  "This chat was started in another Copilot client. It can't be continued in a new session due to security constraints"
const MISSING_SESSION_OWNER = "This chat can't be continued in a new session because its owner could not be determined."

function ReadOnlyChatNotice({
  apiURL,
  messages,
  legacyThreadId,
  legacyThreadName,
}: {
  apiURL: string
  messages: readonly CopilotChatMessage[]
  legacyThreadId: string
  legacyThreadName: string
}) {
  const navigate = useNavigate()
  const [state, setState] = useState<'idle' | 'pending' | 'error'>('idle')
  const referenceOwners = legacyConversationOwners(messages)
  const sessionOwner = referenceOwners[0] ?? getCurrentUserLogin()
  const navigateToTask = useSafeAsyncCallback((taskId: string) => navigate(generateTaskPathForRepo({taskId})))
  const showError = useSafeAsyncCallback(() => {
    setState('error')
    announce(READ_ONLY_CHAT_ERROR)
  })

  if (referenceOwners.length > 1) {
    return <ReadOnlyConversationNotice message={<p>{MULTIPLE_REFERENCE_OWNERS}</p>} />
  }

  if (!sessionOwner) {
    return <ReadOnlyConversationNotice message={<p>{MISSING_SESSION_OWNER}</p>} />
  }

  const handleContinue = async () => {
    setState('pending')
    try {
      const {continueLegacyConversationInTaskChat} = await import('@github-ui/copilot-chat-app/runtime/mission-control')
      const taskId = await continueLegacyConversationInTaskChat({
        apiURL,
        messages: legacyConversationMessages(messages),
        legacyThreadId,
        legacyThreadName,
        owner: sessionOwner,
      })
      navigateToTask(taskId)
    } catch {
      showError()
    }
  }

  return (
    <ReadOnlyConversationNotice
      message={
        <p>
          This chat was started in another Copilot client. You can read it here, but you&apos;ll need a new session to
          continue.
        </p>
      }
      action={
        <Button
          leadingVisual={GitBranchIcon}
          loading={state === 'pending'}
          loadingAnnouncement="Creating a new session"
          onClick={handleContinue}
        >
          Continue in a new session
        </Button>
      }
      error={state === 'error' ? READ_ONLY_CHAT_ERROR : undefined}
    />
  )
}

export function ConversationView(props: ConversationViewProps) {
  return (
    <ConversationViewErrorBoundary>
      <ConversationViewInner {...props} />
    </ConversationViewErrorBoundary>
  )
}

export function ConversationViewErrorBoundary(props: {children: React.ReactNode}) {
  const {selectedThreadID} = useChatStateValues('selectedThreadID')
  const [key, setKey] = useState(0)
  const hasErrored = useRef(false)
  const onError = useCallback(() => {
    hasErrored.current = true
  }, [])

  // If we had an error, and then change threads, change the key to reset the error state.
  useEffect(() => {
    if (hasErrored.current) {
      setKey(prev => prev + 1)
      hasErrored.current = false
    }
  }, [selectedThreadID])

  return (
    <ErrorBoundary
      boundaryName="copilot-conversation"
      key={key}
      fallback={<ErrorFallback regionName="This conversation" />}
      onError={onError}
    >
      {props.children}
    </ErrorBoundary>
  )
}
